import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'

import { Text } from '@wartek-id/text-v1'
import { Button } from '@wartek-id/button-v1'

import {
  useAPIContentKinerjaUtama,
  useAPIKinerja,
} from 'api/kinerja/useAPIKinerja'
import { assetUrl } from 'utils/url'
import { PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { IndikatorPerencanaanSelect } from 'app/KinerjaShared/components/IndikatorPerencanaanSelect'
import { useUpsertPerencanaan } from 'app/KinerjaGpkPage/hooks/use-upsert-perencanaan'
import { useGpkTracker } from 'app/KinerjaGpkPage/hooks/use-gpk-tracker'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'

import type { OptionItem } from 'app/KinerjaShared/components/IndikatorPerencanaanSelect'
import type { UpsertSKPPayload } from 'types/kinerja'

type PerencanaanPegawaiPengembanganKompetensiProps = {
  onNext: () => void
}

export function PerencanaanPegawaiPengembanganKompetensi({
  onNext,
}: PerencanaanPegawaiPengembanganKompetensiProps) {
  const router = useRouter()
  const { trackEvent } = useGpkTracker()
  const { periodYear } = usePeriodContext()

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const { data: perencanaanData, isLoading: isPerencanaanLoading } =
    useAPIKinerja(
      {
        staleTime: 60 * 60 * 1000, // 1 hour,
      },
      {
        tipe: 'GPK',
      }
    )
  // Safely get the first item of the `pengembanganKompetensi`
  // since it always have only 1 item
  const currentPengembanganKompetensi =
    perencanaanData?.data?.pengembanganKompetensi?.[0]

  // -----------------------------------------
  // Handle form data
  // -----------------------------------------
  const [selectedPengembanganKompetensi, setSelectedPengembanganKompetensi] =
    useState<OptionItem>(undefined)

  const isDirty = useMemo(() => {
    return (
      selectedPengembanganKompetensi?.value !==
      currentPengembanganKompetensi?.id
    )
  }, [currentPengembanganKompetensi, selectedPengembanganKompetensi])

  // Sync with perencanaan data
  useEffect(() => {
    if (currentPengembanganKompetensi) {
      setSelectedPengembanganKompetensi({
        value: currentPengembanganKompetensi?.id,
        label: '',
      })
    }
  }, [currentPengembanganKompetensi])

  const isValidated = !!selectedPengembanganKompetensi?.value

  // -----------------------------------------
  // Get Pengembangan Kompetensi content
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
    isSuccess: isSuccessContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'GPK',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )
  const pengembanganKompetensiContent =
    dataContentKinerjaUtama?.data?.pengembanganKompetensi
  const pengembanganKompetensiOptions: Array<OptionItem> = useMemo(
    () =>
      pengembanganKompetensiContent?.map((item) => ({
        value: item.id,
        label: item.rencanaHasilKerja || '',
      })) || [],
    [pengembanganKompetensiContent]
  )

  // -----------------------------------------
  // Handle events tracking
  // -----------------------------------------
  // Track visit event
  useEffect(() => {
    if (isSuccessContentKinerjaUtama) {
      trackEvent({
        activityType: 'view_pengembangan_kompetensi',
        screenName: 'perencanaan_pegawai',
        attributes: {
          year: String(periodYear),
        },
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccessContentKinerjaUtama])

  // Track save action
  const handleTrackSaveAction = () => {
    trackEvent({
      activityType: 'save_pengembangan_kompetensi',
      screenName: 'perencanaan_pegawai',
      attributes: {
        year: String(periodYear),
      },
    })
  }

  // -----------------------------------------
  // Handle actions
  // -----------------------------------------
  const { handleUpsertMutation, isLoading: isLoadingUpsertPerencanaan } =
    useUpsertPerencanaan()
  const payload: UpsertSKPPayload = useMemo(
    () => ({
      id: perencanaanData?.data?.id,
      pengembanganKompetensi: selectedPengembanganKompetensi
        ? [{ id: Number(selectedPengembanganKompetensi.value), kuantitas: 1 }]
        : [{ id: undefined, kuantitas: 0 }],
      tipe: 'GPK',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [perencanaanData, selectedPengembanganKompetensi?.value]
  )

  const handleSaveToDraft = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      shouldShowSuccessMessage: true,
      onSuccess: () => {
        router.push(`${PENGELOLAAN_KINERJA_GPK_PEGAWAI_PATH}`)

        // Track save action if the form is dirty
        if (isDirty) {
          handleTrackSaveAction()
        }
      },
    })

  const handleNext = () =>
    handleUpsertMutation(payload, {
      shouldInvalidateCache: true,
      skip: !isDirty,
      onSuccess: () => {
        onNext()

        // Track save action if the form is dirty
        if (isDirty) {
          handleTrackSaveAction()
        }
      },
    })

  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingQueries = isPerencanaanLoading || isLoadingContentKinerjaUtama
  const isLoading = isLoadingQueries || isLoadingUpsertPerencanaan

  return (
    <>
      <div className="p-9">
        <div className="flex items-start gap-3 mb-6">
          <img
            src={assetUrl('/illustrations/img-document.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={64}
            height={64}
          />
          <div>
            <Text className="max-w-4xl mb-2" variant="heading-md">
              Pilih 1 Indikator untuk menjadi fokus peningkatan kinerja Anda
            </Text>
            <ul className="list-disc list-outside pl-5">
              <Text as="li">
                Diskusi bersama Tim Kinerja terkait kegiatan yang dapat Anda
                lakukan untuk meningkatkan kompetensi berdasarkan indikator yang
                dipilih.
              </Text>
              <Text as="li">
                Di tahap Pelaksanaan, Anda akan melakukan kegiatan sesuai
                diskusi dan mengisi refleksi terhadap kegiatan yang sudah Anda
                lakukan.
              </Text>
            </ul>
          </div>
        </div>
        <IndikatorPerencanaanSelect
          isLoading={isLoading}
          allowReset={true}
          contents={{
            title: 'Indikator Kompetensi',
            orderNumber: 1,
          }}
          options={pengembanganKompetensiOptions}
          selected={selectedPengembanganKompetensi}
          onChange={setSelectedPengembanganKompetensi}
        />
      </div>
      <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
        <Button
          size="lg"
          color="white"
          onClick={handleSaveToDraft}
          disabled={isPerencanaanLoading || !isDirty}
        >
          Simpan draf
        </Button>

        <Button
          color="black"
          size="lg"
          disabled={
            isPerencanaanLoading || isLoadingUpsertPerencanaan || !isValidated
          }
          onClick={handleNext}
        >
          Ke Perilaku Kerja
        </Button>
      </div>
    </>
  )
}
